<script setup lang="ts">
import type { IAiChatCard } from './types'
import { EntityAiChatLeadButton } from '../lead-button'
import { localizedField } from '../utils'

const props = defineProps<{ item: IAiChatCard }>()
const emit = defineEmits<{ navigate: [] }>()
const { t, locale } = useI18n()

const title = computed(() => localizedField(props.item.title, locale.value))
const bank = computed(() => localizedField(props.item.bank, locale.value))

const currencyTypeLabel = computed(() =>
  props.item.currency_type === 'multi'
    ? t('entity.card.item.multi_currency')
    : t('entity.card.item.mono_currency')
)

const typeLabel = computed(() =>
  props.item.type === 'debit' ? t('common.debit') : t('common.credit_card')
)

const serviceFee = computed(() => {
  // null (no data) and 0 both mean free, matching the standard card behaviour
  if (!props.item.service_fee) return t('entity.card.item.free')
  return useCurrencyFormat(props.item.service_fee, 1)
})
</script>

<template>
  <div class="AiCard group">
    <NuxtLink :to="item.url" class="block">
      <div class="min-w-0">
        <div class="truncate text-sm font-semibold text-gray-900 group-hover:text-blue-600">
          {{ title }}
        </div>
        <div class="SecondaryTextHeadings">{{ currencyTypeLabel }} {{ item.payment_system }}</div>
        <div class="text-xs text-gray-500">{{ bank }}</div>
      </div>
      <div class="mt-2 grid grid-cols-3 gap-x-3 gap-y-2">
        <div>
          <div class="SecondaryTextHeadings">{{ $t('entity.card.item.annual_fee') }}</div>
          <div class="text-sm font-semibold">{{ serviceFee }}</div>
        </div>
        <div>
          <div class="SecondaryTextHeadings">{{ $t('common.type') }}</div>
          <div class="text-sm font-semibold">{{ typeLabel }}</div>
        </div>
        <div v-if="item.currencies.length">
          <div class="SecondaryTextHeadings">{{ $t('common.currency') }}</div>
          <div class="text-sm font-semibold">{{ item.currencies.join(', ') }}</div>
        </div>
      </div>
    </NuxtLink>
    <EntityAiChatLeadButton v-if="item.lead_url" :to="item.lead_url" @navigate="emit('navigate')" />
  </div>
</template>
